/** Edit one Kuali module using the compact Configure contract from Fable. */
import { useEffect, useState } from 'react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { Input } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useKualiResearchSelectionPreview } from '../hooks/useKualiResearchSelectionPreview'
import { getKualiResearchFilterFields } from '../services/api'
import type {
  KualiResearchFeedOptions,
  KualiResearchFilterField,
  KualiResearchModuleOptions,
  KualiResearchScope,
} from '../types'
import { KualiResearchFilters } from './KualiResearchFilters'
import { KualiResearchModuleDocuments } from './KualiResearchModuleDocuments'
import { KualiResearchSelectionPreview } from './KualiResearchSelectionPreview'
import { KUALI_RESEARCH_SOURCES } from './KualiResearchSourceSelection'
import { KualiResearchStartingPoint } from './KualiResearchStartingPoint'

const automaticScope = (module: KualiResearchModuleOptions): KualiResearchScope => (
  module.source === 'awards' ? 'all' : 'related-all-awards'
)

const specificScope = (module: KualiResearchModuleOptions): KualiResearchScope => (
  module.source === 'awards' ? 'selected' : 'related-selected-awards'
)

export function KualiResearchModuleScope({
  api,
  orgId,
  vaultId,
  open,
  apiUrl,
  credentialStored,
  credentialSource,
  module,
  onChange,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  open: boolean
  apiUrl: string
  credentialStored: boolean
  credentialSource: KualiResearchFeedOptions['credentialSource']
  module: KualiResearchModuleOptions
  onChange: (module: KualiResearchModuleOptions) => void
}) {
  const definition = KUALI_RESEARCH_SOURCES.find(value => value.value === module.source)
  const automatic = module.scope === 'all' || module.scope === 'related-all-awards'
  const relatedToAwards = module.source !== 'awards'
  const filterScope = automaticScope(module)
  const [fields, setFields] = useState<KualiResearchFilterField[]>([])
  const [loadingFields, setLoadingFields] = useState(false)
  const [fieldsError, setFieldsError] = useState(false)
  const [fieldsRequest, setFieldsRequest] = useState(0)
  const selection = useKualiResearchSelectionPreview({
    api,
    orgId,
    vaultId,
    open,
    enabled: credentialStored && automatic,
    apiUrl,
    module,
    credentialSource,
  })

  useEffect(() => {
    if (!open || !automatic || !credentialStored) {
      setFields([])
      setLoadingFields(false)
      setFieldsError(false)
      return
    }
    let current = true
    setFields([])
    setLoadingFields(true)
    setFieldsError(false)
    void getKualiResearchFilterFields(
      api,
      orgId,
      vaultId,
      apiUrl,
      module.source,
      filterScope,
      credentialSource,
    ).then(value => { if (current) setFields(value) })
      .catch(() => { if (current) setFieldsError(true) })
      .finally(() => { if (current) setLoadingFields(false) })
    return () => { current = false }
  }, [api, apiUrl, automatic, credentialSource, credentialStored, fieldsRequest, filterScope, module.source, open, orgId, vaultId])

  const chooseMode = (mode: 'automatic' | 'specific') => {
    onChange({
      ...module,
      scope: mode === 'automatic' ? automaticScope(module) : specificScope(module),
      identifiers: [],
      filters: mode === 'automatic' ? module.filters : [],
      startAfter: mode === 'automatic' ? module.startAfter : null,
    })
  }
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-slate-400">
        Each enabled module has its own scope below — showing <strong className="font-semibold text-slate-600">{definition?.label}</strong>.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <ModeCard
          selected={automatic}
          label="Automatic"
          description="Continuously syncs awards matching the module filters, on a schedule."
          onSelect={() => chooseMode('automatic')}
        />
        <ModeCard
          selected={!automatic}
          label="Specific awards"
          description="You choose identifiers and pull on demand — no schedule."
          onSelect={() => chooseMode('specific')}
        />
      </div>

      {automatic ? (
        <>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-[12px] font-semibold text-slate-700">
                Filters <span className="font-normal text-slate-400">
                  — {relatedToAwards ? `which Awards seed related ${definition?.label}` : 'which Awards qualify'}
                </span>
              </h4>
              <LimitInput module={module} onChange={onChange} />
            </div>
            <KualiResearchFilters
              fields={fields}
              filters={module.filters}
              loading={loadingFields}
              error={fieldsError}
              onRetry={() => setFieldsRequest(value => value + 1)}
              onChange={filters => onChange({ ...module, filters })}
            />
          </div>
          <KualiResearchStartingPoint module={module} onChange={onChange} />
        </>
      ) : (
        <>
          <p className="rounded-[9px] border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] leading-[18px] text-slate-600">
            No identifiers are stored here. Paste identifiers or load a CSV from <strong className="font-semibold text-slate-800">Manage awards</strong>.
          </p>
          <div className="flex justify-end"><LimitInput module={module} onChange={onChange} /></div>
        </>
      )}

      <KualiResearchModuleDocuments module={module} onChange={onChange} />
      {automatic ? (
        credentialStored ? (
          <div className="space-y-1.5">
            {relatedToAwards && (
              <p className="text-[11.5px] leading-4 text-slate-500">
                Preview shows the Award scope used to discover related {definition?.label}; document totals are known after sync.
              </p>
            )}
            <KualiResearchSelectionPreview
              preview={selection.preview}
              loading={selection.loading}
              error={selection.error}
            />
          </div>
        ) : (
          <p className="rounded-[9px] border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Store or select a system API key to load the live scope preview.
          </p>
        )
      ) : (
        <p className="rounded-[9px] border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Specific awards are added or unsynced in <strong className="font-semibold text-slate-700">Manage awards</strong>; Configure has no saved identifiers to preview.
        </p>
      )}
    </div>
  )
}

function ModeCard({
  selected,
  label,
  description,
  onSelect,
}: {
  selected: boolean
  label: string
  description: string
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex min-h-[72px] items-start gap-2.5 rounded-[10px] border bg-white px-3 py-2.5 text-left',
        selected ? 'border-[1.5px] border-slate-900' : 'border-slate-200',
      )}
      onClick={onSelect}
    >
      <span className={cn(
        'mt-0.5 grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full border-2',
        selected ? 'border-slate-800 bg-slate-800 after:h-1 after:w-1 after:rounded-full after:bg-white' : 'border-slate-300',
      )} />
      <span>
        <span className="block text-[12.5px] font-semibold text-slate-900">{label}</span>
        <span className="mt-px block text-[11.5px] leading-4 text-slate-500">{description}</span>
      </span>
    </button>
  )
}

function LimitInput({ module, onChange }: ModuleProps) {
  const label = KUALI_RESEARCH_SOURCES.find(value => value.value === module.source)?.label
  return (
    <label className="flex items-center gap-1.5 text-[12px] text-slate-400">
      Cap
      <Input
        aria-label={`${label} sync limit`}
        type="number"
        min={1}
        max={1_000_000}
        value={module.limit || ''}
        onChange={event => onChange({ ...module, limit: Number(event.target.value) })}
        className="h-[29px] w-[68px] rounded-[8px] px-2 text-[12px] text-slate-800"
      />
    </label>
  )
}

interface ModuleProps {
  module: KualiResearchModuleOptions
  onChange: (module: KualiResearchModuleOptions) => void
}
